import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import wordmarkWhite from "@/assets/techtail-wordmark-pixel.png";
import { submitLead } from "@/lib/leadSubmit";
import { divisionFromPath, isAcceptableEmail, isValidPhone } from "@/lib/leadGuard";
import { countryNameEs, isCountryCode } from "@/lib/countries";
import { CountrySelect, HoneypotField } from "./LeadFields";
import { useLeadSuccess } from "@/hooks/useLeadSuccess";

const PixelArrow = () => (
  <svg viewBox="0 0 12 12" className="h-4 w-4" fill="currentColor" shapeRendering="crispEdges">
    <rect x="1" y="5" width="7" height="2" />
    <rect x="7" y="3" width="2" height="1" />
    <rect x="7" y="8" width="2" height="1" />
    <rect x="9" y="5" width="2" height="2" />
  </svg>
);

type Answers = {
  nombre: string;
  empresa: string;
  web: string;
  pais: string;
  ciudad: string;
  territorio: string;
  actividad: string;
  anios: string;
  personas: string;
  tecnicos: string;
  equipo: string;
  soporte: string;
  clientes: string[];
  cuentas: string;
  vertical: string;
  competencia: string;
  demo: string;
  operador: string;
  plazo: string;
  canal: string;
  cargo: string;
  email: string;
  telefono: string;
  linkedin: string;
  motivo: string;
  actividadOtra: string;
  clientesOtra: string;
  canalOtra: string;
};


const emptyAnswers: Answers = {
  nombre: "",
  empresa: "",
  web: "",
  pais: "",
  ciudad: "",
  territorio: "",
  actividad: "",
  anios: "",
  personas: "",
  tecnicos: "",
  equipo: "",
  soporte: "",
  clientes: [],
  cuentas: "",
  vertical: "",
  competencia: "",
  demo: "",
  operador: "",
  plazo: "",
  canal: "",
  cargo: "",
  email: "",
  telefono: "",
  linkedin: "",
  motivo: "",
  actividadOtra: "",
  clientesOtra: "",
  canalOtra: "",
};

// Canonical (backend) values stay in Spanish; labels are translated at render time.
const ACTIVIDADES = [
  { value: "Integrador de tecnología para retail", key: "integrador" },
  { value: "Distribuidor de equipamiento industrial", key: "distribuidor" },
  { value: "Vending u operación de máquinas", key: "vending" },
  { value: "Instalador o ingeniería", key: "instalador" },
  { value: "Operador de tiendas o de espacios", key: "operador" },
  { value: "Consultoría o desarrollo de negocio", key: "consultoria" },
  { value: "Otra cosa", key: "otra" },
];

const EQUIPOS = [
  { value: "Sí, con técnicos en plantilla", key: "propio" },
  { value: "Sí, con una red de instaladores externos", key: "externo" },
  { value: "No, subcontrataríamos cada proyecto", key: "subcontrata" },
  { value: "No, solo vendemos", key: "soloVenta" },
];

const SOPORTES = [
  { value: "Sí, ya lo hacemos con otros productos", key: "si" },
  { value: "Podríamos montarlo", key: "podriamos" },
  { value: "No", key: "no" },
];

const CLIENTES = [
  { value: "Gasolineras y redes de combustible", key: "gasolineras" },
  { value: "Hoteles y turismo", key: "hoteles" },
  { value: "Gimnasios y deportivos", key: "gimnasios" },
  { value: "Oficinas y coworkings", key: "oficinas" },
  { value: "Universidades y campus", key: "universidades" },
  { value: "Industria y fabricación", key: "industria" },
  { value: "Hospitales y clínicas", key: "hospitales" },
  { value: "Retail y alimentación", key: "retail" },
  { value: "Otra cosa", key: "otra" },
];

const CUENTAS = [
  { value: "Menos de 10", key: "menos10" },
  { value: "De 10 a 50", key: "de10a50" },
  { value: "De 50 a 200", key: "de50a200" },
  { value: "Más de 200", key: "mas200" },
];

const VERTICALES = [
  { value: "Retail", key: "retail" },
  { value: "Industrial", key: "industrial" },
  { value: "Medical", key: "medical" },
  { value: "Las tres", key: "todas" },
];

const COMPETENCIA = [
  { value: "No", key: "no" },
  { value: "Sí, vending o máquinas automáticas", key: "vending" },
  { value: "Sí, tienda autónoma o visión artificial", key: "autonoma" },
  { value: "Sí, gestión de almacén o inventario", key: "almacen" },
  { value: "Preferimos comentarlo", key: "comentarlo" },
];

const DEMOS = [
  { value: "Sí, la compraríamos", key: "compraria" },
  { value: "Sí, si hay condiciones de lanzamiento", key: "lanzamiento" },
  { value: "Preferiríamos empezar vendiendo sin unidad", key: "sinUnidad" },
  { value: "No", key: "no" },
];

const OPERADOR = [
  { value: "Sí, nos interesa el modelo de operador", key: "interesa" },
  { value: "Quizá más adelante", key: "adelante" },
  { value: "No, solo distribución", key: "no" },
];

const PLAZOS = [
  { value: "Este trimestre", key: "trimestre" },
  { value: "Este año", key: "anio" },
  { value: "El año que viene", key: "anioQueViene" },
  { value: "Estamos explorando", key: "explorando" },
];

const CANALES = [
  { value: "Alguien nos presentó", key: "presento" },
  { value: "Una feria", key: "feria" },
  { value: "LinkedIn", key: "linkedin" },
  { value: "Prensa", key: "prensa" },
  { value: "Buscando proveedor", key: "buscando" },
  { value: "Otra cosa", key: "otra" },
];

const OTRA_COSA = "Otra cosa";

const STEPS = [
  "intro",
  "nombre",
  "empresa",
  "territorio",
  "actividad",
  "tamano",
  "equipo",
  "soporte",
  "clientes",
  "cuentas",
  "vertical",
  "competencia",
  "demo",
  "operador",
  "plazo",
  "canal",
  "datos",
  "motivo",
  "final",
] as const;
type StepId = (typeof STEPS)[number];

const OptionButton = ({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full text-left border px-5 py-4 text-base md:text-lg transition-colors ${
      selected
        ? "border-brand-blue bg-brand-blue text-black"
        : "border-white/25 text-white hover:border-white hover:bg-white/5"
    }`}
  >
    {label}
  </button>
);

const inputClass =
  "w-full border-b border-white/30 bg-transparent py-3 text-xl md:text-2xl text-white placeholder:text-white/35 focus:border-brand-blue focus:outline-none";

const Question = ({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) => (
  <div className="w-full max-w-3xl">
    <h2 className="font-serif text-2xl md:text-4xl leading-tight text-white">{title}</h2>
    {hint && <p className="mt-3 text-sm md:text-base text-white/50">{hint}</p>}
    <div className="mt-8 space-y-3">{children}</div>
  </div>
);

interface Props {
  open: boolean;
  onClose: () => void;
}

const PartnerForm = ({ open, onClose }: Props) => {
  const onSuccess = useLeadSuccess();
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const [honeypot, setHoneypot] = useState("");
  const startedAt = useRef(0);
  const [a, setA] = useState<Answers>(emptyAnswers);
  const [index, setIndex] = useState(0);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const schema = z.object({
    nombre: z.string().trim().min(1).max(100),
    empresa: z.string().trim().min(1).max(100),
    email: z.string().trim().max(255).refine(isAcceptableEmail),
    pais: z.string().trim().refine(isCountryCode),
    telefono: z.string().trim().max(30).refine(isValidPhone),
  });

  const step: StepId = STEPS[Math.min(index, STEPS.length - 1)];
  const total = STEPS.length - 2;

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    setA(emptyAnswers);
    setIndex(0);
    setError(null);
    setHoneypot("");
    startedAt.current = Date.now();
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const next = () => setIndex((i) => Math.min(i + 1, STEPS.length - 1));
  const back = () => setIndex((i) => Math.max(i - 1, 0));
  const set = <K extends keyof Answers>(k: K, v: Answers[K]) => setA((p) => ({ ...p, [k]: v }));
  const pick = <K extends keyof Answers>(k: K, v: Answers[K]) => {
    set(k, v);
    setTimeout(next, 180);
  };
  const toggleCliente = (v: string) =>
    setA((p) => ({
      ...p,
      clientes: p.clientes.includes(v) ? p.clientes.filter((x) => x !== v) : [...p.clientes, v],
    }));

  const name = a.nombre.trim();

  const canContinue = () => {
    switch (step) {
      case "nombre":
        return name.length > 0;
      case "empresa":
        return a.empresa.trim().length > 0 && a.pais !== "";
      case "territorio":
        return a.territorio.trim().length > 0;
      case "tamano":
        return a.anios.trim().length > 0 && a.personas.trim().length > 0;
      case "clientes":
        return a.clientes.length > 0;
      case "datos":
        return isAcceptableEmail(a.email) && isValidPhone(a.telefono);
      default:
        return true;
    }
  };

  const submit = async () => {
    const parsed = schema.safeParse({
      nombre: a.nombre,
      empresa: a.empresa,
      email: a.email,
      pais: a.pais,
      telefono: a.telefono,
    });
    if (!parsed.success) {
      setError(t("sFormsPartner.validationEmail"));
      return;
    }
    setSending(true);
    setError(null);

    const mensaje = [
      "CANDIDATURA A LA RED DE PARTNERS",
      `Web: ${a.web || "—"}`,
      `Ciudad: ${a.ciudad || "—"}`,
      `Territorio de interés: ${a.territorio}`,
      `Actividad: ${a.actividad === OTRA_COSA ? `Otra cosa — ${a.actividadOtra || "—"}` : a.actividad || "—"}`,
      `Años: ${a.anios || "—"} · Personas: ${a.personas || "—"} · Equipo técnico: ${a.tecnicos || "—"}`,
      `Equipo de instalación: ${a.equipo || "—"}`,
      `Soporte 24/7: ${a.soporte || "—"}`,
      `Clientes: ${a.clientes.join(", ") || "—"}${a.clientes.includes(OTRA_COSA) && a.clientesOtra ? ` (${a.clientesOtra})` : ""}`,
      `Cuentas directas: ${a.cuentas || "—"}`,
      `Vertical: ${a.vertical || "—"}`,
      `Producto competidor: ${a.competencia || "—"}`,
      `Unidad de demostración: ${a.demo || "—"}`,
      `Modelo operador: ${a.operador || "—"}`,
      `Plazo: ${a.plazo || "—"}`,
      `Nos conoció por: ${a.canal === OTRA_COSA ? `Otra cosa — ${a.canalOtra || "—"}` : a.canal || "—"}`,

      `Cargo: ${a.cargo || "—"} · Teléfono: ${a.telefono || "—"} · LinkedIn: ${a.linkedin || "—"}`,
      `Por qué encajarían: ${a.motivo || "—"}`,
    ].join("\n");

    const division = divisionFromPath(pathname);
    const res = await submitLead({
      core: {
        nombre: parsed.data.nombre,
        empresa: parsed.data.empresa,
        email: parsed.data.email,
        telefono: parsed.data.telefono,
        pais: countryNameEs(parsed.data.pais),
        mensaje,
      },
      extras: {
        division,
        form_name: "partner",
        request_type: "partner",
        cargo: a.cargo.trim() || null,
        ciudad: a.ciudad.trim() || null,
        country_code: parsed.data.pais,
      },
      lang: i18n.language,
      honeypot,
      startedAt: startedAt.current,
    });
    setSending(false);
    if (!res.ok) {
      setError(t("sFormsPartner.errorSubmit"));
      return;
    }
    onClose();
    onSuccess("partner", undefined, { division, request_type: "partner", spam: res.spam });
  };

  const stepNumber = STEPS.indexOf(step);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[200] bg-black flex flex-col"
          role="dialog"
          aria-modal="true"
        >
          <HoneypotField value={honeypot} onChange={setHoneypot} />
          <div className="flex items-center justify-between px-5 md:px-10 py-5">
            <div className="flex items-center gap-4">
              <img src={wordmarkWhite} alt="TECHTAIL" className="h-5 md:h-6 w-auto" />
              {step !== "intro" && step !== "final" && (
                <span className="font-dot text-[11px] md:text-xs uppercase tracking-widest text-brand-blue">
                  {`// ${String(stepNumber).padStart(2, "0")} / ${total}`}
                </span>
              )}
            </div>
            {step !== "intro" && (
              <button
                type="button"
                onClick={onClose}
                className="text-white/60 text-sm hover:text-white transition-colors"
              >
                {t("sFormsPartner.close")}
              </button>
            )}
          </div>

          <div className="h-px w-full bg-white/10">
            <div
              className="h-px bg-brand-blue transition-all duration-300"
              style={{ width: `${(stepNumber / (STEPS.length - 1)) * 100}%` }}
            />
          </div>

          <div className="flex-1 overflow-y-auto flex flex-col">
            <div className="container mx-auto px-5 md:px-10 py-8 md:py-12">
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -18 }}
                  transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                  className="w-full"
                >
                  {step === "intro" && (
                    <div className="max-w-2xl">
                      <h2 className="font-serif text-3xl md:text-5xl leading-tight text-white">
                        {t("sFormsPartner.intro.title")}
                      </h2>
                      <p className="mt-5 text-base md:text-lg text-white/60 max-w-xl">
                        {t("sFormsPartner.intro.text")}
                      </p>
                      <button
                        type="button"
                        onClick={next}
                        className="mt-10 inline-flex items-center gap-3 rounded-none bg-brand-blue pl-6 pr-2 py-2 text-black text-base"
                        >
                        {t("sFormsPartner.start")}
                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-none bg-black text-brand-blue">
                          <PixelArrow />
                        </span>
                      </button>
                    </div>
                  )}

                  {step === "nombre" && (
                    <Question title={t("sFormsPartner.nombre.title")}>
                      <input
                        autoFocus
                        value={a.nombre}
                        maxLength={100}
                        onChange={(e) => set("nombre", e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && canContinue() && next()}
                        placeholder={t("sFormsPartner.nombre.placeholder")}
                        className={inputClass}
                      />
                    </Question>
                  )}

                  {step === "empresa" && (
                    <Question title={`${t("sFormsPartner.empresa.titleBase")}${name ? `, ${name}` : ""}?`}>
                      <input
                        autoFocus
                        value={a.empresa}
                        maxLength={100}
                        onChange={(e) => set("empresa", e.target.value)}
                        placeholder={t("sFormsPartner.empresa.empresaPlaceholder")}
                        className={inputClass}
                      />
                      <input
                        value={a.web}
                        maxLength={200}
                        onChange={(e) => set("web", e.target.value)}
                        placeholder={t("sFormsPartner.empresa.webPlaceholder")}
                        className={inputClass}
                      />
                      <CountrySelect value={a.pais} onChange={(v) => set("pais", v)} className={`${inputClass} appearance-none rounded-none`} />
                      <input
                        value={a.ciudad}
                        maxLength={100}
                        onChange={(e) => set("ciudad", e.target.value)}
                        placeholder={t("sFormsPartner.empresa.ciudadPlaceholder")}
                        className={inputClass}
                      />
                    </Question>
                  )}

                  {step === "territorio" && (
                    <Question
                      title={t("sFormsPartner.territorio.title")}
                      hint={t("sFormsPartner.territorio.hint")}
                    >
                      <input
                        autoFocus
                        value={a.territorio}
                        maxLength={300}
                        onChange={(e) => set("territorio", e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && canContinue() && next()}
                        placeholder={t("sFormsPartner.territorio.placeholder")}
                        className={inputClass}
                      />
                    </Question>
                  )}

                  {step === "actividad" && (
                    <Question title={t("sFormsPartner.actividad.title")}>
                      {ACTIVIDADES.map((o) => (
                        <OptionButton
                          key={o.value}
                          label={t(`sFormsPartner.actividad.options.${o.key}`)}
                          selected={a.actividad === o.value}
                          onClick={() => (o.value === OTRA_COSA ? set("actividad", o.value) : pick("actividad", o.value))}
                        />
                      ))}
                      {a.actividad === OTRA_COSA && (
                        <input
                          autoFocus
                          value={a.actividadOtra}
                          maxLength={140}
                          onChange={(e) => set("actividadOtra", e.target.value)}
                          placeholder={t("sFormsPartner.actividad.placeholder")}
                          className={inputClass}
                        />
                      )}
                    </Question>

                  )}

                  {step === "tamano" && (
                    <Question title={t("sFormsPartner.tamano.title")}>
                      <input
                        autoFocus
                        value={a.anios}
                        maxLength={20}
                        onChange={(e) => set("anios", e.target.value)}
                        placeholder={t("sFormsPartner.tamano.aniosPlaceholder")}
                        className={inputClass}
                      />
                      <input
                        value={a.personas}
                        maxLength={20}
                        onChange={(e) => set("personas", e.target.value)}
                        placeholder={t("sFormsPartner.tamano.personasPlaceholder")}
                        className={inputClass}
                      />
                      <input
                        value={a.tecnicos}
                        maxLength={20}
                        onChange={(e) => set("tecnicos", e.target.value)}
                        placeholder={t("sFormsPartner.tamano.tecnicosPlaceholder")}
                        className={inputClass}
                      />
                    </Question>
                  )}

                  {step === "equipo" && (
                    <Question title={t("sFormsPartner.equipo.title")}>
                      {EQUIPOS.map((o) => (
                        <OptionButton
                          key={o.value}
                          label={t(`sFormsPartner.equipo.options.${o.key}`)}
                          selected={a.equipo === o.value}
                          onClick={() => pick("equipo", o.value)}
                        />
                      ))}
                    </Question>
                  )}

                  {step === "soporte" && (
                    <Question title={t("sFormsPartner.soporte.title")}>
                      {SOPORTES.map((o) => (
                        <OptionButton
                          key={o.value}
                          label={t(`sFormsPartner.soporte.options.${o.key}`)}
                          selected={a.soporte === o.value}
                          onClick={() => pick("soporte", o.value)}
                        />
                      ))}
                    </Question>
                  )}

                  {step === "clientes" && (
                    <Question title={t("sFormsPartner.clientes.title")} hint={t("sFormsPartner.clientes.hint")}>
                      {CLIENTES.map((o) => (
                        <OptionButton
                          key={o.value}
                          label={t(`sFormsPartner.clientes.options.${o.key}`)}
                          selected={a.clientes.includes(o.value)}
                          onClick={() => toggleCliente(o.value)}
                        />
                      ))}
                      {a.clientes.includes(OTRA_COSA) && (
                        <input
                          autoFocus
                          value={a.clientesOtra}
                          maxLength={140}
                          onChange={(e) => set("clientesOtra", e.target.value)}
                          placeholder={t("sFormsPartner.clientes.placeholder")}
                          className={inputClass}
                        />
                      )}
                    </Question>

                  )}

                  {step === "cuentas" && (
                    <Question title={t("sFormsPartner.cuentas.title")}>
                      {CUENTAS.map((o) => (
                        <OptionButton
                          key={o.value}
                          label={t(`sFormsPartner.cuentas.options.${o.key}`)}
                          selected={a.cuentas === o.value}
                          onClick={() => pick("cuentas", o.value)}
                        />
                      ))}
                    </Question>
                  )}

                  {step === "vertical" && (
                    <Question title={t("sFormsPartner.vertical.title")}>
                      {VERTICALES.map((o) => (
                        <OptionButton
                          key={o.value}
                          label={t(`sFormsPartner.vertical.options.${o.key}`)}
                          selected={a.vertical === o.value}
                          onClick={() => pick("vertical", o.value)}
                        />
                      ))}
                    </Question>
                  )}

                  {step === "competencia" && (
                    <Question title={t("sFormsPartner.competencia.title")}>
                      {COMPETENCIA.map((o) => (
                        <OptionButton
                          key={o.value}
                          label={t(`sFormsPartner.competencia.options.${o.key}`)}
                          selected={a.competencia === o.value}
                          onClick={() => pick("competencia", o.value)}
                        />
                      ))}
                    </Question>
                  )}

                  {step === "demo" && (
                    <Question title={t("sFormsPartner.demo.title")}>
                      {DEMOS.map((o) => (
                        <OptionButton
                          key={o.value}
                          label={t(`sFormsPartner.demo.options.${o.key}`)}
                          selected={a.demo === o.value}
                          onClick={() => pick("demo", o.value)}
                        />
                      ))}
                    </Question>
                  )}

                  {step === "operador" && (
                    <Question title={t("sFormsPartner.operador.title")}>
                      {OPERADOR.map((o) => (
                        <OptionButton
                          key={o.value}
                          label={t(`sFormsPartner.operador.options.${o.key}`)}
                          selected={a.operador === o.value}
                          onClick={() => pick("operador", o.value)}
                        />
                      ))}
                    </Question>
                  )}

                  {step === "plazo" && (
                    <Question title={t("sFormsPartner.plazo.title")}>
                      {PLAZOS.map((o) => (
                        <OptionButton
                          key={o.value}
                          label={t(`sFormsPartner.plazo.options.${o.key}`)}
                          selected={a.plazo === o.value}
                          onClick={() => pick("plazo", o.value)}
                        />
                      ))}
                    </Question>
                  )}

                  {step === "canal" && (
                    <Question title={t("sFormsPartner.canal.title")}>
                      {CANALES.map((o) => (
                        <OptionButton
                          key={o.value}
                          label={t(`sFormsPartner.canal.options.${o.key}`)}
                          selected={a.canal === o.value}
                          onClick={() => (o.value === OTRA_COSA ? set("canal", o.value) : pick("canal", o.value))}
                        />
                      ))}
                      {a.canal === OTRA_COSA && (
                        <input
                          autoFocus
                          value={a.canalOtra}
                          maxLength={140}
                          onChange={(e) => set("canalOtra", e.target.value)}
                          placeholder={t("sFormsPartner.canal.placeholder")}
                          className={inputClass}
                        />
                      )}
                    </Question>

                  )}

                  {step === "datos" && (
                    <Question title={t("sFormsPartner.datos.title")}>
                      <input
                        autoFocus
                        value={a.cargo}
                        maxLength={100}
                        onChange={(e) => set("cargo", e.target.value)}
                        placeholder={t("sFormsPartner.datos.cargoPlaceholder")}
                        className={inputClass}
                      />
                      <input
                        type="email"
                        value={a.email}
                        maxLength={255}
                        onChange={(e) => set("email", e.target.value)}
                        placeholder={t("sFormsPartner.datos.emailPlaceholder")}
                        className={inputClass}
                      />
                      <input
                        type="tel"
                        value={a.telefono}
                        maxLength={30}
                        onChange={(e) => set("telefono", e.target.value)}
                        placeholder={t("sFormsPartner.datos.telefonoPlaceholder")}
                        className={inputClass}
                      />
                      <input
                        value={a.linkedin}
                        maxLength={200}
                        onChange={(e) => set("linkedin", e.target.value)}
                        placeholder={t("sFormsPartner.datos.linkedinPlaceholder")}
                        className={inputClass}
                      />
                    </Question>
                  )}

                  {step === "motivo" && (
                    <Question
                      title={t("sFormsPartner.motivo.title")}
                      hint={t("sFormsPartner.motivo.hint")}
                    >
                      <textarea
                        autoFocus
                        value={a.motivo}
                        maxLength={1000}
                        rows={4}
                        onChange={(e) => set("motivo", e.target.value)}
                        placeholder={t("sFormsPartner.motivo.placeholder")}
                        className={`${inputClass} resize-none`}
                      />
                    </Question>
                  )}

                  {step === "final" && (
                    <div className="max-w-2xl">
                      <h2 className="font-serif text-3xl md:text-5xl leading-tight text-white">
                        {t("sFormsPartner.final.title")}{name ? `, ${name}` : ""}
                      </h2>
                      <p className="mt-5 text-base md:text-lg text-white/60">
                        {t("sFormsPartner.final.text")}
                      </p>
                      <button
                        type="button"
                        onClick={onClose}
                        className="mt-10 inline-flex items-center gap-3 rounded-none bg-brand-blue pl-6 pr-2 py-2 text-black text-base"
                      >
                        {t("sFormsPartner.close")}
                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-none bg-black text-brand-blue">
                          <PixelArrow />
                        </span>
                      </button>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {step !== "intro" && step !== "final" && (
            <div className="px-5 md:px-10 pb-8 pt-2 flex items-center justify-between gap-4">
              <button
                type="button"
                onClick={back}
                className="text-sm text-white/50 hover:text-white transition-colors"
              >
                {t("sFormsPartner.back")}
              </button>
              <div className="flex items-center gap-4">
                {error && <span className="text-sm text-white/70">{error}</span>}
                <button
                  type="button"
                  disabled={!canContinue() || sending}
                  onClick={step === "motivo" ? submit : next}
                  className="inline-flex items-center gap-3 rounded-none bg-brand-blue pl-6 pr-2 py-2 text-black text-sm md:text-base disabled:opacity-40"
                >
                  {step === "motivo" ? (sending ? t("sFormsPartner.sending") : t("sFormsPartner.submit")) : t("sFormsPartner.continue")}
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-none bg-black text-brand-blue">
                    <PixelArrow />
                  </span>
                </button>
              </div>
            </div>
          )}
          {step === "intro" && (
            <div className="absolute inset-x-0 bottom-0 z-10 flex justify-center px-5 pb-[max(2rem,env(safe-area-inset-bottom))] pt-4">
              <button
                type="button"
                aria-label={t("sFormsPartner.close")}
                onClick={onClose}
                className="h-12 w-12 flex items-center justify-center border border-white/30 text-white hover:text-brand-blue transition-colors"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-6 w-6"><path d="M18 6 6 18M6 6l12 12" /></svg>
              </button>

            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PartnerForm;
